import React from "react";
import {
  Typography,
  Button,
  Input,
  Avatar,
  IconButton,
  Tooltip,
} from "@material-tailwind/react";
import Swal from "sweetalert2";
import Toaster from "../../components/Toaster";
import { Link } from "react-router-dom";
import Pagination from "../../components/Pagination";
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  CheckBadgeIcon,
  ChevronDownIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  FunnelIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/solid";
import { Popover } from "react-tiny-popover";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import ShowLoader from "@/components/Loader";
import {
  getInternalUser,
  deleteInternalUser,
  approveInternalUser,
  setSelectedInternalUser,
  fetchOffice,
  bulkActionUpdate,
  fetchOfficeCity,
  fetchDeliveryPartnerCities,
} from "../../store/slices/internalUsersSlice";
import useHasPermission from "../../components/useHasPermission";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { downloadDeliveryPartnerReport } from "@/store/slices/deliveryUserSlice";
import { handleDownloadURL } from "@/components/DownloadFile";


const TYPE = "Delivery Partner";
const PAGE_URL = "delivery-partner";
const PAGE_TITLE = "Delivery Partner";
const typeLabelMap = {
  "Delivery Partner": "delivery_partner",
};

export function ListDeliveryPartners() {
  const { t } = useTranslation("internal-user");
  const typeLabel = typeLabelMap[TYPE] || TYPE;
  var action_options = [
    { label: t('active'), value: "active" },
    { label: t('inactive'), value: "inactive" },
  ];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [city, setCity] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
  const [isCheckedItemPopoverOpen, setIsCheckedItemPopoverOpen] =
    useState(false);
  const [checkedAction, setCheckedAction] = useState("");
  const [checkedItems, setCheckedItems] = useState([]);
  const [activeTab, setActiveTab] = useState("all");

  const hasAddPermission = useHasPermission(`${PAGE_URL}.add`);
  const hasViewPermission = useHasPermission(`${PAGE_URL}.view`);
  const hasUpdatePermission = useHasPermission(`${PAGE_URL}.update`);
  const hasDeletePermission = useHasPermission(`${PAGE_URL}.delete`);
  const { dashboardData, downloadLoading } = useSelector((state) => state.home);
  const { user } = useSelector((state) => state.user);

  const currentUserRole = user?.roles?.[0];
  const canDownload = ['Administrator', 'Sub Admin', 'Finance'].includes(currentUserRole);


  useEffect(() => {
    dispatch(fetchOffice(""));
    // dispatch(fetchOfficeCity());
    dispatch(fetchDeliveryPartnerCities());
  }, [dispatch]);

  useEffect(() => {
    action_options = action_options.filter(
      (option) => option.value !== "deleted"
    );
    if (hasDeletePermission) {
      action_options.push({ label: t('delete'), value: "deleted" });
    }
  }, [hasDeletePermission]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setItemsPerPage(10);
    setSearch("");
    setFilter("");
  };

  useEffect(() => {
    dispatch(
      getInternalUser({
        type: TYPE,
        activetab: activeTab,
        page: currentPage,
        perPage: itemsPerPage,
        search,
        filter,
        city,
      })
    );
  }, [dispatch, city, currentPage, itemsPerPage, activeTab]);

  const { list_users, offices, offices_city, loading, list_loading, delivery_partner_cities } =
    useSelector((state) => state.internalUser);

  const handelSearch = (event) => {
    const value = event.target.value;

    if (event.key === "Enter" && value) {
      dispatch(
        getInternalUser({
          type: TYPE,
          activetab: activeTab,
          page: currentPage,
          perPage: itemsPerPage,
          search,
          filter,
          city,
        })
      );
    } else if (event.key === "Backspace" && value.length === 0) {
      dispatch(
        getInternalUser({
          type: TYPE,
          activetab: activeTab,
          page: 1,
          perPage: itemsPerPage,
          search: "",
          filter,
          city,
        })
      );
    }
  };

  const handleSearchClick = () => {
    dispatch(
      getInternalUser({
        type: TYPE,
        activetab: activeTab,
        page: 1,
        perPage: itemsPerPage,
        search,
        filter,
        city,
      })
    );
  };

  const handelRemoveSearch = () => {
    setSearch("");
    dispatch(
      getInternalUser({
        type: TYPE,
        activetab: activeTab,
        page: 1,
        perPage: itemsPerPage,
        search: "",
        filter,
        city,
      })
    );
  };

  const handelFilter = (value) => {
    setFilter(value);
    dispatch(
      getInternalUser({
        type: TYPE,
        activetab: activeTab,
        page: currentPage,
        perPage: itemsPerPage,
        search,
        filter: value,
        city,
      })
    );
  };

  const handelEdit = (id, mode = "edit") => {
    if (id) {
      const user = list_users?.users?.find(
        (row) => String(row.id) === String(id)
      );
      dispatch(setSelectedInternalUser(user));
      navigate(`/dashboard/${PAGE_URL}/${mode}/${id}`);
    }
  };

  const handelCheckedItems = (action) => {
    setCheckedAction(action);
    setIsCheckedItemPopoverOpen(false);
    if (checkedItems.length) {
      Swal.fire({
        title: t('are_you_sure'),
        text: t('do_you_really_want_to_perform_this_action'),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: t('yes'),
        cancelButtonText: t('cancel')
      }).then((result) => {
        if (result.isConfirmed) {
          dispatch(bulkActionUpdate({ status: action, ids: checkedItems }))
            .unwrap()
            .then((res) => {
              if (res.status) {
                const pastTenseMap = {
                  active: "activated",
                  inactive: "inactivated",
                  approved: "approved",
                  rejected: "rejected",
                  deleted: "deleted",
                };
                const pastTenseMsg = pastTenseMap[action] ?? action + "ed";
                Toaster.success(`${TYPE} ${pastTenseMsg} ${t('successfully')}`);
                setCheckedItems([]);
                setCheckedAction("");
                dispatch(
                  getInternalUser({
                    type: TYPE,
                    activetab: activeTab,
                    page: currentPage,
                    perPage: itemsPerPage,
                    search,
                    filter,
                    city,
                  })
                );
              } else {
                Toaster.error(res.message);
              }
            })
            .catch(() => {
              Toaster.error(t('failed_to_perform_action'));
            });
        }
      });
    } else {
      Toaster.error(t('you_need_to_select_an_item_to_perform_this_action'));
    }
  };

  const handleSingleCheck = (value) => {
    if (checkedItems.includes(value)) {
      setCheckedItems(checkedItems.filter((v) => v !== value));
    } else {
      setCheckedItems([...checkedItems, value]);
    }
  };

  const handleCheckAll = (e) => {
    if (e.target.checked) {
      const ids = list_users?.users?.map((row) => row.id) || [];
      setCheckedItems(ids);
    } else {
      setCheckedItems([]);
    }
  };

  const isAllChecked =
    list_users?.users?.length > 0 &&
    checkedItems.length === list_users.users.length;

  const delete_row = (id) => {
    Swal.fire({
      title: t('are_you_sure'),
      text: t('you_wont_be_able_to_revert_this'),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t('yes_delete_it'),
      cancelButtonText: t('cancel')
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteInternalUser(id))
          .unwrap()
          .then((res) => {
            if (res.status) {
              Toaster.success(`${TYPE} ${t('deleted_successfully')}`);
              dispatch(
                getInternalUser({
                  type: TYPE,
                  activetab: activeTab,
                  page: currentPage,
                  perPage: itemsPerPage,
                  search,
                  filter,
                  city,
                })
              );
            } else {
              Toaster.error(res.message);
            }
          })
          .catch(() => {
            Toaster.error(t('delete_failed'));
          });
      }
    });
  };

  const [isPopoverOpen, setIsPopoverOpen] = useState([]);
  const openActionPopover = (id) => {
    const key = id;
    setIsPopoverOpen((prev) => {
      if (prev.includes(key)) {
        return [];
      }
      return [key];
    });
  };

  const popoverRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setIsPopoverOpen([]);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const approveRejectInternalUser = async (id, value) => {
    var msg =
      value == "approved"
        ? "approve"
        : value == "rejected"
          ? "reject"
          : value;
    let actionVerbs = ["approve", "reject"];
    let text;
    if (actionVerbs.includes(msg.toLowerCase())) {
      text = t('do_you_want_to_action_this_user', { action: msg, user: TYPE.toLowerCase() });
    } else {
      text = t('do_you_want_to_make_this_user_status', { user: TYPE.toLowerCase(), status: msg });
    }
    Swal.fire({
      title: t('are_you_sure'),
      text: `${text}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t('yes'),
      cancelButtonText: t('cancel')
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(approveInternalUser({ id, value }))
          .unwrap()
          .then((res) => {
            const pastTenseMap = {
              active: "activated",
              inactive: "inactivated",
              approved: "approved",
              rejected: "rejected",
            };
            const pastTenseMsg = pastTenseMap[res.status] ?? res.status + "ed";
            Toaster.success(`${TYPE} ${pastTenseMsg} ${t('successfully')}`);
            dispatch(
              getInternalUser({
                type: TYPE,
                activetab: activeTab,
                page: currentPage,
                perPage: itemsPerPage,
                search,
                filter,
                city,
              })
            );
          })
          .catch(() => {
            Toaster.error(t('changes_failed'));
          });
        setIsPopoverOpen([]);
      }
    });
  };

  const renderTableHeader = (headers) => {
    return headers.map((el, index) => (
      <th key={el} className="border-b border-blue-gray-50 py-3 px-5 text-left">
        {index === 0 ? (
          <div className="flex items-center gap-4">
            {hasUpdatePermission && (
              <input
                type="checkbox"
                className="w-4 h-4 accent-green cursor-pointer"
                onChange={handleCheckAll}
                checked={isAllChecked}
              />
            )}
            <Typography
              variant="small"
              className="text-[11px] font-bold text-black-gray"
            >
              {el}
            </Typography>
          </div>
        ) : (
          <Typography
            variant="small"
            className="text-[11px] font-bold text-black-gray"
          >
            {el}
          </Typography>
        )}
      </th>
    ));
  };

  const tableColSpan = 9;

  // for downloading delivery partner report
  const handleDownloadReport = async () => {
    try {
      const resultAction = await dispatch(downloadDeliveryPartnerReport()).unwrap();

      if (resultAction?.status && resultAction?.data?.download_url) {
        await handleDownloadURL({
          fileUrl: resultAction.data.download_url,
        });
      }
    } catch (err) {
      console.error("Failed to download report:", err);
    }
  };

  return (
    <div className="mt-5 mb-8 flex flex-col gap-8">
      {loading && <ShowLoader />}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0 px-4 md:px-6">
        <div className="w-full md:flex-1">
          <Typography variant="h5" color="blue-gray">
            {t('list_title', { type: t(typeLabel) })} ({list_users?.pagination?.total || 0})
          </Typography>
        </div>
        <div className="w-full md:flex-1 flex justify-center">
          <div className="w-full max-w-md relative">
            <Input
              label={t('search_placeholder')}
              className={search ? "pr-10" : ""}
              icon={
                !search ? <MagnifyingGlassIcon className="h-5 w-5" /> : ""
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyUp={handelSearch}
            />
            {search && (
              <IconButton
                variant="text"
                size="sm"
                className="!absolute right-1 top-1"
                onClick={handelRemoveSearch}
              >
                <XCircleIcon className="h-5 w-5 text-red-500" />
              </IconButton>
            )}
          </div>
          {search && (
            <Button
              variant="outlined"
              className="flex items-center border-gray-300 capitalize font-bold bg-black text-white mx-2"
              onClick={handleSearchClick}
            >
              <MagnifyingGlassIcon className="w-4 h-4 mr-1" /> {t('search_button')}
            </Button>
          )}
        </div>
        {hasAddPermission && (
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 px-4 md:px-6">
            <Link to={`/dashboard/${PAGE_URL}/add`}>
              <Button
                variant="outlined"
                className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white"
              >
                <PlusCircleIcon className="w-4 h-4" /> {t('add_button', { page_title: t(typeLabel) })}
              </Button>
            </Link>
          </div>
        )}

        {/* Black Download Button (Role Restricted) */}

        {canDownload && (
          <button
            onClick={handleDownloadReport}
            disabled={downloadLoading}
            className="flex items-center gap-2 bg-black hover:bg-gray-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-sm transition-all duration-200 disabled:opacity-50"
            title="Download Report"
          >
            <ArrowDownTrayIcon className="w-4 h-4 text-white" />
            <span>
              {downloadLoading ? t('downloading') : t('download_report')}
            </span>
          </button>
        )}

      </div>

      <div className="mb-4 px-4 md:px-6">
        <div className="w-full md:w-auto relative">
          <div className="flex bg-gray-100 rounded-2xl p-1 shadow-sm relative inline-flex">
            <button
              type="button"
              onClick={() => switchTab("all")}
              className={`relative text-sm font-semibold px-6 py-2 rounded-2xl transition-colors duration-300 z-10
                ${activeTab === "all" ? "text-white" : "text-gray-700 hover:text-black"}`}
            >
              {t('existing_partners_tab', { type: t(typeLabel) })}{" "}
              {activeTab !== "all" && (
                <span className="text-gray-400 text-xs ml-1">
                  ({list_users?.totalExisting ?? 0})
                </span>
              )}
              {activeTab === "all" && (
                <span className="absolute inset-0 bg-black rounded-2xl z-[-1] transition-all duration-300" />
              )}
            </button>
            <button
              type="button"
              onClick={() => switchTab("new")}
              className={`relative text-sm font-semibold px-6 py-2 rounded-2xl transition-colors duration-300 z-10
                ${activeTab === "new" ? "text-white" : "text-gray-700 hover:text-black"}`}
            >
              {t('new_request_tab')}{" "}
              {activeTab !== "new" && (
                <span className="text-gray-400 text-xs ml-1">
                  ({list_users?.totalNewRequest ?? 0})
                </span>
              )}
              {activeTab === "new" && (
                <span className="absolute inset-0 bg-black rounded-2xl z-[-1] transition-all duration-300" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0 px-4 md:px-6">
        <div className="flex w-full md:w-auto items-center gap-3 flex-wrap justify-start md:justify-start">
          <Typography variant="h6" color="blue-gray">
            {t(typeLabel)} ({list_users?.pagination?.total || 0})
          </Typography>
        </div>
        <div className="flex w-full md:w-auto items-center gap-3 flex-wrap justify-end md:justify-end">
          <Select
            placeholder={t('select_city_placeholder')}
            onChange={(c) => {
              if (c) {
                setCity(c.value);
              } else {
                setCity("");
              }
            }}
            options={delivery_partner_cities}
            isClearable
          />
          {/* <Popover
            isOpen={isFilterPopoverOpen}
            positions={["bottom", "left", "right"]}
            padding={4}
            onClickOutside={() => setIsFilterPopoverOpen(false)}
            content={
              <div className="bg-white shadow-xl rounded-md w-48 flex flex-col z-50 border border-gray-100 py-1">
                <button
                  onClick={() => {
                    handelFilter("");
                    setIsFilterPopoverOpen(false);
                  }}
                  className={`text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 ${filter === "" ? "bg-gray-100 font-semibold" : ""}`}
                >
                  All
                </button>
                {offices?.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      handelFilter(option.value);
                      setIsFilterPopoverOpen(false);
                    }}
                    className={`text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 ${filter === option.value ? "bg-gray-100 font-semibold" : ""}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            }
          >
            <Button
              onClick={() => setIsFilterPopoverOpen(!isFilterPopoverOpen)}
              variant="outlined"
              className="flex items-center gap-2 border-gray-300 text-gray-800 capitalize font-medium"
            >
              <FunnelIcon className="h-4 w-4" /> Filter{" "}
              {filter && `(${filter})`}
            </Button>
          </Popover> */}
          {hasUpdatePermission && (
            <Popover
              isOpen={isCheckedItemPopoverOpen}
              positions={["bottom"]}
              padding={4}
              onClickOutside={() => setIsCheckedItemPopoverOpen(false)}
              content={
                <div className="bg-white shadow-xl rounded-md w-48 flex flex-col z-50 border border-gray-100 py-1">
                  {action_options?.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        handelCheckedItems(option.value);
                      }}
                      className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 capitalize"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              }
            >
              <Button
                onClick={() =>
                  setIsCheckedItemPopoverOpen(!isCheckedItemPopoverOpen)
                }
                variant="outlined"
                className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white"
              >
                {t('selected_items_button')} {checkedAction && `(${checkedAction})`}{" "}
                <ChevronDownIcon className="h-4 w-4" />
              </Button>
            </Popover>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px] table-auto text-left">
          <thead>
            <tr>
              {renderTableHeader([
                t('emp_id_header'),
                t('full_name_header'),
                t('contact_number_header'),
                t('email_header'),
                t('location_header'),
                t('status_header'),
                "",
              ])}
            </tr>
          </thead>
          <tbody>
            {list_loading && (
              <tr>
                <td colSpan={tableColSpan} className="text-center">
                  {" "}
                  <div role="status">
                    <svg
                      aria-hidden="true"
                      className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-green-500"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  </div>
                </td>
              </tr>
            )}

            {list_users?.users?.length > 0 ? (
              list_users?.users.map(
                (
                  {
                    id,
                    profile_image,
                    first_name,
                    last_name,
                    mobile_number,
                    country_code,
                    delivery_location,
                    email,
                    kyc_details,
                    status,
                    on_time_rate,
                    performance_status,
                  },
                  key
                ) => {
                  const className = `py-3 px-4 min-w-[170px]   ${key === list_users.users.length - 1
                    ? ""
                    : "border-b border-blue-gray-50"
                    }`;

                  return (
                    <tr key={id}>
                      <td className={className}>
                        <div className="flex items-center gap-4">
                          {hasUpdatePermission && (
                            <input
                              type="checkbox"
                              className="w-4 h-4 accent-green cursor-pointer"
                              onChange={() => handleSingleCheck(id)}
                              checked={checkedItems.includes(id)}
                            />
                          )}
                          <Avatar
                            src={
                              profile_image ? profile_image : "/img/icons/user-profile.png"
                            }
                            alt={first_name}
                            size="sm"
                            variant="rounded"
                          />
                          <div>
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-semibold"
                            >
                              ID {String(id).padStart(4, "0")}
                            </Typography>
                          </div>
                        </div>
                      </td>
                      <td className={className}>
                        <div className="flex items-center gap-1 w-[150px] justify-between">
                          <div className="flex items-center gap-1">
                            <Typography className="text-xs font-semibold text-blue-gray-600 max-w-[120px] truncate">
                              {first_name} {last_name ? last_name : ''}
                            </Typography>

                            {/* Performance Arrow Indicator */}
                            {performance_status && performance_status !== 'NO_DATA' && (
                              <Tooltip
                                content={`On-time delivery rate: ${on_time_rate ?? 0}%`}
                                placement="top"
                                className="bg-white text-gray-900 border border-gray-200 shadow-md text-xs px-2 py-1 font-sans"
                              >
                                <span
                                  className={`inline-flex items-center justify-center text-[15px] font-extrabold cursor-pointer transition-transform ${performance_status === 'GOOD'
                                    ? 'text-green-600'
                                    : 'text-red-600 animate-bounce'
                                    }`}
                                >
                                  {performance_status === 'GOOD' ? '▲' : '▼'}
                                </span>
                              </Tooltip>
                            )}
                          </div>

                          <Tooltip
                            content={t('zone_tooltip', { zone: delivery_location ? delivery_location?.country_name + ", " + delivery_location?.city_name : t('alternate_phone_not_available') })}
                            placement="top"
                            className="bg-white text-gray-900 border border-gray-200 shadow-md"
                          >
                            <InformationCircleIcon className=" svg-info text-blue-gray-500 cursor-pointer" />
                          </Tooltip>
                        </div>
                      </td>
                      <td className={className}>
                        {country_code} {mobile_number}
                      </td>
                      <td className={className}>{email}</td>
                      <td className={className}>{delivery_location?.country_name + ", " + delivery_location?.city_name}</td>
                      <td className={className}>
                        {/* <span
                          className={`inline-flex items-center rounded-xl px-2 py-1 text-xs font-medium inset-ring inset-ring-green-600/20
                            ${
                              status === "pending"
                                ? "bg-yellow-50 text-yellow-800"
                                : status === "active"
                                  ? "bg-green-50 text-green-700"
                                  : "bg-red-50 text-red-700"
                            }`}
                        > */}

                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs font-semibold
                            ${status === "active"
                              ? "text-green-600"
                              : status === "inactive"
                                ? "text-amber-600"
                                : status === "pending"
                                  ? "text-red-500"
                                  : "text-red-500"
                            }`}
                        >
                          {t(status) || ""}
                        </span>
                      </td>
                      <td className={className}>
                        <div className="flex items-center space-x-2">
                          <Popover
                            isOpen={isPopoverOpen.includes(id)}
                            positions={["bottom", "left"]}
                            padding={4}
                            content={
                              <div
                                ref={popoverRef}
                                className="bg-white shadow-xl rounded-md w-32 flex flex-col z-50 border border-gray-100 py-1"
                              >
                                {hasViewPermission && (
                                  <button
                                    className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                    onClick={() => handelEdit(id, "view")}
                                  >
                                    <EyeIcon className="h-4 w-4 text-blue-500"></EyeIcon>
                                    {t('view_action')}
                                  </button>
                                )}
                                {hasUpdatePermission && (
                                  <button
                                    className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                    onClick={() => handelEdit(id, "edit")}
                                  >
                                    <PencilSquareIcon className="h-4 w-4 text-blue-500"></PencilSquareIcon>
                                    {t('edit_action')}
                                  </button>
                                )}
                                {hasDeletePermission && (
                                  <button
                                    className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                    onClick={() => delete_row(id)}
                                  >
                                    <TrashIcon className="h-4 w-4 text-red-500"></TrashIcon>
                                    {t('delete_action')}
                                  </button>
                                )}
                                {hasUpdatePermission && (
                                  <>
                                    {status === "pending" && (
                                      <>
                                        <button
                                          className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                          onClick={() =>
                                            approveRejectInternalUser(
                                              id,
                                              "approved"
                                            )
                                          }
                                        >
                                          <CheckBadgeIcon className="h-4 w-4 text-blue-500" />
                                          {t('approve_action')}
                                        </button>
                                        <button
                                          className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                          onClick={() =>
                                            approveRejectInternalUser(
                                              id,
                                              "rejected"
                                            )
                                          }
                                        >
                                          <XCircleIcon className="h-4 w-4 text-red-500" />
                                          {t('reject_action')}
                                        </button>
                                      </>
                                    )}
                                    {status === "rejected" && (
                                      <button
                                        className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                        onClick={() =>
                                          approveRejectInternalUser(
                                            id,
                                            "approved"
                                          )
                                        }
                                      >
                                        <CheckBadgeIcon className="h-4 w-4 text-blue-500" />
                                        {t('approve_action')}
                                      </button>
                                    )}
                                    {status === "active" && (
                                      <button
                                        className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                        onClick={() =>
                                          approveRejectInternalUser(
                                            id,
                                            "inactive"
                                          )
                                        }
                                      >
                                        <XCircleIcon className="h-4 w-4 text-red-500" />
                                        {t('inactivate_action')}
                                      </button>
                                    )}
                                    {status === "inactive" && (
                                      <button
                                        className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                        onClick={() =>
                                          approveRejectInternalUser(
                                            id,
                                            "active"
                                          )
                                        }
                                      >
                                        <CheckBadgeIcon className="h-4 w-4 text-blue-500" />
                                        {t('activate_action')}
                                      </button>
                                    )}
                                    {status === "delete_requested" && (
                                      <button
                                        className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                        onClick={() =>
                                          approveRejectInternalUser(
                                            id,
                                            "active"
                                          )
                                        }
                                      >
                                        <CheckBadgeIcon className="h-5 w-5 text-blue-500" />
                                        {t('revoke_request_active')}
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            }
                          >
                            <IconButton
                              variant="text"
                              color="blue-gray"
                              onClick={() => openActionPopover(id)}
                            >
                              <EllipsisVerticalIcon className="h-6 w-6" />
                            </IconButton>
                          </Popover>
                        </div>
                      </td>
                    </tr>
                  );
                }
              )
            ) : (
              !list_loading && (
                <tr>
                  <td
                    colSpan={tableColSpan}
                    className="text-center py-3"
                  >
                    {t('no_data_found')}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
      {list_users?.pagination && list_users?.pagination.total > 0 && (
        <Pagination
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          setCurrentPage={setCurrentPage}
          setItemsPerPage={setItemsPerPage}
          totalPages={list_users?.pagination?.last_page}
        />
      )}
    </div>
  );
}

export default ListDeliveryPartners;
